import './common.css'

import React, { useEffect, useRef, useState } from 'react'
import { Services } from '@/services/services'
import { CurrentSession } from '@/services/repository/session'
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { addServerErrors } from '@/components/forms/common/FormBase'
import { useForm } from 'react-hook-form'
import { PageQuery, PageResponse, SearchTable, SearchTableParams, SearchTableRef } from '@/components/search/SearchTable'
import { useModal } from '@/components/modal/Modal'
import { formatDate } from '@/common/time'
import { isDesktopScreen, isLargeScreen, useResponsiveChange } from '@/common/device'
import { BackButton } from '@/components/layout/NavBar'
import { CommonUsernameInput, UsernameInput } from '@/components/forms/common/user/input'
import { Spinner } from '@/components/spinner'
import { UCIDInput } from '@/components/forms/common/cards/input'
import { CardFormData, ManagerRemoveCardPopup, SimpleCard } from './common'
import { useParams } from 'react-router-dom'

export interface ManagerEditCardPopupProps {
  services: Services
  session: CurrentSession
  onClose: () => void
  ucid: string | undefined
}

export interface ManagerInsertCardPopupProps {
  services: Services
  session: CurrentSession
  username: string
  onClose: () => void
}

export const ManagerEditCardPopup = ({ services, session, onClose, ucid }: ManagerEditCardPopupProps) => {
  const form = useForm<CardFormData>()
  const [data, setData] = useState<CardFormData>()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!ucid) return
    setData(undefined)
    services.session.fetchWithToken(`/api/communal/cards/byid/${ucid}`).then(r => r.json()).then((data) => {
      setData(data)
      form.reset(data)
    })
  }, [ucid])

  const handle = (data: CardFormData): Promise<void> => {
    if (!data.username) {
      console.error('username is required')
      return Promise.reject()
    }
    if (!form) return Promise.reject()
    return services.session.fetchWithToken(`/api/communal/cards/byid/${ucid}/update`, {
      method: 'PUT',
      body: JSON.stringify(form.getValues())
    }).then(async (r) => {
      if (r.status !== 200) {
        setError('Write data error')
        const errors = await r.json()
        addServerErrors(form, session.translates, errors)
        return
      }
      setError(null)
      onClose()
    })
  }

  return data
    ? <form className="main-form" onSubmit={form.handleSubmit(handle)}>
      <div className="form-field" >
        <label htmlFor="user">{session.translates.for("forms.card.fields.userId.title")}:</label>
        <UsernameInput
          fieldName="username"
          formName="card"
          form={form}
          services={services}
          translates={session.translates}
          readOnly={true}
          required={true}
        />
      </div>
      <div className="form-field">
        <label htmlFor="ucid">{session.translates.for("forms.card.fields.ucid.title")}:</label>
        <UCIDInput
          fieldName="ucid"
          formName="card"
          form={form}
          services={services}
          translates={session.translates}
          readOnly={false}
          required={true}
        />
      </div>
      {error && <div className="critic-error-box">{session.translates.for("errors.general_error")}</div>}
      <button className="submit-button" type="submit" disabled={
        !data.username || !data.ucid
      }>
        {session.translates.for("forms.card.submit")}
      </button>
    </form>
    : <div className="spinner"></div>
}

export const ManagerInsertCardPopup = ({ services, session, onClose, username }: ManagerInsertCardPopupProps) => {
  const form = useForm<CardFormData>({
    mode: "onChange",
    defaultValues: {
      ucid: '',
      username
    }
  })
  const [error, setError] = useState<string | null>(null)

  const handle = (data: CardFormData): Promise<void> => {
    if (!data.username) {
      console.error('user is required')
      return Promise.reject()
    }
    if (!data.ucid) {
      console.error('ucid is required')
      return Promise.reject()
    }
    if (!form) return Promise.reject()
    setError(null)
    return services.session.fetchWithToken(`/api/communal/cards/new`, {
      method: 'POST',
      body: JSON.stringify(data)
    }).then(async (r) => {
      if (r.status !== 200) {
        setError('Write data error')
        const errors = await r.json()
        addServerErrors(form, session.translates, errors)
        return
      }
      setError(null)
      form.reset()
      onClose()
    })
  }

  if (!session || !services) {
    return <Spinner />
  }

  return <form className="main-form" onSubmit={form.handleSubmit(handle)}>
    <div className="form-field" >
      <label htmlFor="user">{session.translates.for("forms.card.fields.userId.title")}:</label>
      <UsernameInput
        fieldName="username"
        formName="card"
        form={form}
        services={services}
        translates={session.translates}
        readOnly={true}
        required={true}
      />
    </div>
    <div className="form-field">
      <label htmlFor="ucid">{session.translates.for("forms.card.fields.ucid.title")}:</label>
      <UCIDInput
        fieldName="ucid"
        formName="card"
        form={form}
        services={services}
        translates={session.translates}
        readOnly={false}
        required={true}
      />
    </div>
    {/* <div className="form-field">
      <label htmlFor="label">{session.translates.for("forms.card.fields.label.title")}:</label>
      <input
        type="text"
        id="label"
        {...form.register('label')}
        name="label"
      />
    </div> */}
    {error && <div className="critic-error-box">{session.translates.for("errors.general_error")}</div>}
    <button className="submit-button" type="submit" disabled={!form.formState.isValid}>
      {session.translates.for("forms.card.submit")}
    </button>
  </form>
}

export const UserManagerCardsPage = ({ services, session }: { services: Services, session: CurrentSession }) => {
  const user = useParams().user
  const searchTableRef = useRef<SearchTableRef>(null)
  const { openModal } = useModal()

  useResponsiveChange()

  const tableParams: SearchTableParams<SimpleCard> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleCard>> => {
      return services.session.fetchWithToken(`/api/communal/cards/search?user=${user}&pageNumber=${q.pageNumber || ''}&pageSize=${q.pageSize || ''}&search=${q.search || ''}`).then(r => {
        if (r.status === 200) return r.json()
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 30,
      search: ""
    },
    toolbox: () => <>
      <div className='tool' onClick={() => openModal((props) => ManagerInsertCardPopup({ ...props, services, session, username: user }))}><i className='svgfont-add' /></div>
    </>,
    header: () => <>
      <div className='hcol'>{session.translates.for('elements.table.cards.ucid')}</div>
      <div className='hcol'>{session.translates.for('elements.table.cards.username')}</div>
      {/* <div className='hcol'>{session.translates.for('elements.table.cards.label')}</div> */}
      <div className='hcol'></div>
      <div className='hcol'></div>
    </>,
    row: (row) => <>
      <div className='col'>{row.ucid}</div>
      <div className='col'>{row.username}</div>
      {/* <div className='col'>{row.label}</div> */}
      <div className='col icocol' onClick={() => openModal((props) => ManagerEditCardPopup({ ...props, ucid: row.ucid, services, session }))}>
        <i className='svgfont-edit' />
      </div>
      <div className='col icocol' onClick={() => openModal((props) => ManagerRemoveCardPopup({ ...props, card: row, services, session }))}>
        <i className='svgfont-remove' />
      </div>
    </>,
    translates: session.translates
  }

  return <LoggedInPageLayout pageName='communal-cards' services={services} session={session}
    navbarLeft={<BackButton services={services} translates={session.translates} />}>
      <h1>{user}</h1>
      <SearchTable ref={searchTableRef} {...tableParams} />
  </LoggedInPageLayout>
}